// The REVIEW's "Hours priced" (PRICING.md "Which hours are priced"; mock-up
// docs/mockups/approved-hours-status.html): per area, the measured hours, the
// PM's, and the review's choice of which price the job — Measured (the
// default), Assessed (the PM's hours fixed per surface when chosen, #607) or Other (typed, with a reason). The
// choice is the area's, shared by every version and option; an issued version
// keeps the hours it was priced from.
import {
  APPROVED_SOURCES,
  approvedChange,
  approvedSummary,
  parseSurfaces,
  serialiseSurfaces,
  setApprovedReason,
  withApprovedChoice,
  APPROVED_REASON_MAX,
  type ApprovedHours,
  type ApprovedSource,
  type SectionInfo,
  type VarianceBand,
  variance,
} from '@higgins/m2q-pricing';
import { useState } from 'react';
import { patchFields, type DeviceRecord, type Fields } from '../lib/device-db';
import { advanceMeasure } from '../lib/measure-status-save';
import { syncNow } from '../lib/outbox';
import { sourceLabel } from './hours-priced-text';
import { NumberField } from './number-field';
import { SavedTextField } from './saved-text-field';
import { formatHours } from './surface-format';

const BAND_STYLE: Record<VarianceBand, string> = {
  green: 'bg-green-100 text-green-900',
  amber: 'bg-amber-100 text-amber-900',
  red: 'bg-red-100 text-red-900',
  none: 'bg-slate-100 text-slate-500',
};

/** How far the PM's hours are from the measure: green within 15 %, amber within 30 %, red beyond. Only here — PM assess is blind (#627). */
function VarianceBadge({ measured, assessed }: { measured: number; assessed: number | null | undefined }) {
  const { percent, band } = variance(measured, assessed);
  const label = percent === null ? 'n/a' : `${percent > 0 ? '+' : ''}${Math.round(percent)}%`;
  return <span className={`inline-block min-w-14 rounded-full px-2 py-0.5 text-center text-xs tabular-nums ${BAND_STYLE[band]}`}>{label}</span>;
}

interface Props {
  info: ReadonlyMap<string, SectionInfo>;
  records: readonly DeviceRecord[];
  measure: DeviceRecord;
  /** An issued or replaced version: shown, not changed. */
  frozen: boolean;
}

interface Row {
  area: DeviceRecord;
  info: SectionInfo;
  hours: ApprovedHours;
}

/** The area's surfaces document with its Other reason set ('' clears it), applied to the copy stored now. */
const withReason = (stored: Fields, reason: string) => serialiseSurfaces(setApprovedReason(parseSurfaces(stored.hig_surfaces as string | null), reason));

/**
 * A choice is part of the review, so the measure moves on to In review (never back). Other is saved only with its
 * reason, in the same change (DD, 2026-10-26); Measured or Assessed clears any earlier reason. Assessed fixes the PM's
 * hours per surface as they are stored now (#607); Measured or Other clears them.
 */
async function choose(row: Row, measure: DeviceRecord, source: ApprovedSource, other?: { hours: number | null; reason: string }) {
  const reason = source === 'Other' ? (other?.reason ?? row.hours.reason) : '';
  if (source === 'Other' && !reason.trim()) return;
  await patchFields(row.area.id, (stored) => ({
    ...approvedChange(source, row.hours, other?.hours),
    hig_surfaces: serialiseSurfaces(withApprovedChoice(parseSurfaces(stored.hig_surfaces as string | null), source, reason)),
  }));
  void syncNow();
  await advanceMeasure(measure, 'In review');
}

async function saveReason(area: DeviceRecord, reason: string) {
  await patchFields(area.id, (stored) => ({ hig_surfaces: withReason(stored, reason) }));
  void syncNow();
}

function SourceChoice({ row, onOther, measure }: { row: Row; onOther: () => void; measure: DeviceRecord }) {
  const { hours } = row;
  return (
    <div className="inline-flex overflow-hidden rounded-md border border-slate-300" role="radiogroup" aria-label={`Hours priced, ${row.info.name}`}>
      {APPROVED_SOURCES.map((source) => {
        const on = hours.source === source && (hours.chosen || source === 'Measured');
        // Assessed needs PM hours to fix (#607): with none it isn't offered, even on an area already chosen as Assessed.
        const unavailable = source === 'Assessed' && hours.assessed === null;
        return (
          <button
            key={source}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={unavailable}
            onClick={() => (source === 'Other' && !on ? onOther() : void choose(row, measure, source))}
            className={`border-l border-slate-300 px-2.5 py-1 text-xs first:border-l-0 ${on ? 'bg-primary font-medium text-primary-foreground' : 'bg-white text-slate-700'} disabled:text-slate-300`}
          >
            {source}
          </button>
        );
      })}
    </div>
  );
}

/** Choosing Other: the hours and the reason first; nothing is saved until there is a reason. */
function OtherDraft({ row, measure, onDone }: { row: Row; measure: DeviceRecord; onDone: () => void }) {
  const [hours, setHours] = useState<number | null>(row.hours.hours);
  const [reason, setReason] = useState('');
  async function use() {
    await choose(row, measure, 'Other', { hours, reason });
    onDone();
  }
  return (
    <div className="space-y-1 rounded border border-slate-300 bg-slate-50 p-2 text-xs">
      <span className="flex items-center gap-1">
        Other hours
        <NumberField compact label={`Other hours, ${row.info.name}`} value={hours} onChange={setHours} className="w-16 rounded border border-slate-400 bg-white text-right" />h
      </span>
      <SavedTextField compact label={`Reason for other hours, ${row.info.name}`} value={reason} onSave={setReason} placeholder="Reason (required)" maxLength={APPROVED_REASON_MAX} className="w-full rounded border border-slate-300 bg-white text-xs" />
      <span className="flex items-center gap-2">
        <button type="button" disabled={!reason.trim()} onClick={() => void use()} className="rounded bg-primary px-2 py-1 font-medium text-primary-foreground disabled:opacity-40">
          Use these hours
        </button>
        <button type="button" onClick={onDone} className="text-slate-600 underline">
          Cancel
        </button>
        {!reason.trim() && <span className="text-slate-500">Give a reason to use them.</span>}
      </span>
    </div>
  );
}

/** Other hours already chosen: change the hours or the reason; a reason can't be emptied (choose Measured or Assessed instead). */
function OtherChosen({ row, measure }: { row: Row; measure: DeviceRecord }) {
  const { info, hours } = row;
  const [blank, setBlank] = useState(false);
  function saveText(text: string) {
    setBlank(!text.trim());
    if (text.trim()) void saveReason(row.area, text);
  }
  return (
    <div className="space-y-1">
      <span className="flex items-center gap-1 text-xs">
        <NumberField compact label={`Other hours, ${info.name}`} value={hours.hours} onChange={(v) => void choose(row, measure, 'Other', { hours: v, reason: hours.reason })} className="w-16 rounded border border-slate-400 bg-white text-right" />h
      </span>
      <SavedTextField compact label={`Reason for other hours, ${info.name}`} value={hours.reason} onSave={saveText} placeholder="Reason (required)" maxLength={APPROVED_REASON_MAX} className="w-full rounded border border-slate-300 bg-white text-xs" />
      {blank && <p className="text-xs text-red-700">A reason is required for Other hours, so it wasn&rsquo;t cleared. To drop them, choose Measured or Assessed.</p>}
      {hours.missingReason && !blank && <p className="text-xs text-red-700">Give a reason for these hours: nothing can be accepted, proposed or issued until there is one.</p>}
    </div>
  );
}

function HoursRow({ row, measure }: { row: Row; measure: DeviceRecord }) {
  const { info, hours } = row;
  const [drafting, setDrafting] = useState(false);
  const other = hours.source === 'Other' && hours.chosen;
  return (
    <tr className="border-t border-slate-200 align-top">
      <td className="px-3 py-1.5">
        {info.building} · {info.name}
        {info.count > 1 && <span className="text-slate-500"> × {info.count}</span>}
      </td>
      <td className="px-2 py-1.5 text-right tabular-nums">{formatHours(hours.measured)}</td>
      <td className="px-2 py-1.5 text-right tabular-nums">{hours.assessed === null ? '—' : formatHours(hours.assessed)}</td>
      <td className="px-2 py-1.5">
        <VarianceBadge measured={hours.measured} assessed={hours.assessed} />
      </td>
      <td className="space-y-1 px-2 py-1.5">
        {info.washOnly ? (
          <span className="text-xs text-slate-500">Wash only: priced from the wash m²</span>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <SourceChoice row={row} measure={measure} onOther={() => setDrafting(true)} />
              {hours.needsChoice && <span className="text-xs text-amber-800">not chosen</span>}
            </div>
            {hours.changed && hours.assessed !== null && (
              <p className="text-xs text-amber-800">PM hours changed since chosen (now {formatHours(hours.assessed)} with measured for the rest). Choose Assessed again to price them.</p>
            )}
            {hours.stale && <p className="text-xs text-red-700">Assessed was chosen before the PM&rsquo;s hours were fixed per surface. Choose Assessed again: nothing can be accepted, proposed or issued until then.</p>}
            {drafting && !other && <OtherDraft row={row} measure={measure} onDone={() => setDrafting(false)} />}
            {other && <OtherChosen row={row} measure={measure} />}
          </>
        )}
      </td>
      <td className="px-2 py-1.5 text-right font-medium tabular-nums">{info.washOnly ? '—' : formatHours(hours.hours)}</td>
    </tr>
  );
}

export function HoursPriced({ info, records, measure, frozen }: Props) {
  // An issued or replaced version keeps the hours it was priced from (its snapshot): no choices to show or make.
  if (frozen) return <p className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">This version keeps the hours it was priced from. Choices made on the current draft reach a new version.</p>;
  const rows: Row[] = [...info.entries()].flatMap(([id, i]) => {
    const area = records.find((r) => r.id === id);
    return area && i.hours ? [{ area, info: i, hours: i.hours }] : [];
  });
  if (!rows.length) return null;
  const { waiting } = approvedSummary(rows.map((r) => r.hours));
  const priced = rows.filter((r) => !r.info.washOnly);
  const total = (pick: (h: ApprovedHours) => number) => priced.reduce((sum, r) => sum + pick(r.hours) * r.info.count, 0);
  const notChosen = rows.filter((r) => r.hours.needsChoice).map((r) => r.info.name);

  return (
    <section className="space-y-2">
      {waiting > 0 && (
        <p className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          {waiting === 1 ? '1 area has' : `${waiting} areas have`} PM hours with no choice yet ({notChosen.join(', ')}). {waiting === 1 ? 'It’s' : 'They’re'} priced from measured hours until you choose.
        </p>
      )}
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-xs text-slate-500">
              <th className="px-3 py-1.5 text-left font-semibold text-slate-800">Hours priced</th>
              <th className="px-2 py-1.5 text-right font-normal">Measured</th>
              <th className="px-2 py-1.5 text-right font-normal" title="The PM's hours where entered, measured for the rest">
                {sourceLabel('Assessed')}
              </th>
              <th className="px-2 py-1.5" />
              <th className="px-2 py-1.5 text-left font-normal">Price from</th>
              <th className="px-2 py-1.5 text-right font-normal">Priced h</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <HoursRow key={row.area.id} row={row} measure={measure} />
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-slate-300 font-medium">
              <td className="px-3 py-1.5">Priced hours (before down time, building counts applied)</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{formatHours(total((h) => h.measured))}</td>
              <td colSpan={3} />
              <td className="px-2 py-1.5 text-right tabular-nums">{formatHours(total((h) => h.hours))}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

/** The Summary's line: how this option's areas are priced, and any PM hours waiting for the REVIEW. */
export function HoursPricedLine({ info }: { info: ReadonlyMap<string, SectionInfo> }) {
  const areas = [...info.values()].flatMap((i) => (i.hours && !i.excluded && !i.washOnly ? [i.hours] : []));
  if (!areas.length) return null;
  const { waiting, line } = approvedSummary(areas);
  return (
    <p className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
      Hours priced from: {line}
      {waiting > 0 && <span className="text-amber-800"> · {waiting === 1 ? '1 area’s' : `${waiting} areas’`} PM hours not chosen yet (REVIEW)</span>}
    </p>
  );
}
